import BulletList from '@tiptap/extension-bullet-list';
import {Color} from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import OrderedList from '@tiptap/extension-ordered-list';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import {useEditor} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {noop} from 'lodash';
import React from 'react';
import styled from 'styled-components';

import {useLinkNavigation} from '../../../../../src/components/common/navigation/useLinkNavigation';
import {Trans} from '../../../../shared/helpers/src/internationalization/react';
import {Button} from '../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {Icon} from '../../../../shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../shared/ui/src/theme/userThemeProvider';
import {buildListPath} from '../../../store/src/panel/knowledgeBasePanelRoutes';
import {ArticleEditor} from '../components/editor/articleEditor';
import {Toolbar} from '../components/editor/toolbar';
import {PageHeader} from '../components/pageHeader';

/*
 * Constants.
 */

const editorWidth = 632; // px

/*
 * Style.
 */

const StyledPageDiv = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StyledScrollableDiv = styled.div`
  overflow: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const StyledContentWrapperDiv = styled.div`
  display: flex;
  height: inherit;
  flex: 1;
`;

const StyledEditorWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
  padding-left: 48px;
  padding-top: 24px;
  width: ${editorWidth}px;
`;

const StyledFooterDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0px 48px;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${(p) => p.theme.foreground};
`;

const StyledTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  display: flex;
  background-color: ${(p) => p.theme.foreground};
  justify-content: flex-end;
  padding-top: 16px;
  padding-bottom: 16px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledToolbarWrapperDiv = styled.div`
  width: ${editorWidth}px;
`;

/*
 * Component.
 */

export const Article = () => {
  const {navigateToLink} = useLinkNavigation();
  const theme = useUserTheme();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: {
          HTMLAttributes: {
            class: 'kb-blockquote',
          },
        },
      }),
      OrderedList,
      BulletList,
      Underline,
      Image.configure({
        inline: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'kb-link',
        },
      }),
      Highlight.configure({multicolor: true}),
      TextStyle,
      Color,
    ],
    content: 'Hello World',
  });

  if (!editor) {
    return <>Loading...</>;
  }

  return (
    <StyledPageDiv>
      <PageHeader
        title={
          <StyledTitle>
            <Icon name="articleFilled" color={theme.greys.shade70} />
            <Trans id="knowledge-base-article-page-header">Add article</Trans>
          </StyledTitle>
        }
        topLevelBreadcrumb={{
          label: <Trans id="knowledge-base-article-page-header-breadcrumb">Content</Trans>,
          onBreadcrumbClick: () => navigateToLink(buildListPath()),
        }}
        interactionId="knowledge-base-article-page-header"
      />
      <StyledScrollableDiv>
        <StyledContentWrapperDiv>
          <StyledEditorWrapperDiv>
            <ArticleEditor editor={editor} />
          </StyledEditorWrapperDiv>
        </StyledContentWrapperDiv>
        <StyledFooterDiv>
          <StyledToolbarWrapperDiv>
            <Toolbar editor={editor} />
          </StyledToolbarWrapperDiv>
          <StyledButtonGroup>
            <Button isRounded onClick={() => navigateToLink(buildListPath())}>
              <Trans id="knowledge-base-article-page-cancel-button">Cancel</Trans>
            </Button>
            <Button isRounded variant={ButtonVariantsEnum.PRIMARY} onClick={noop}>
              <Trans id="knowledge-base-article-page-save-button">Save draft</Trans>
            </Button>
          </StyledButtonGroup>
        </StyledFooterDiv>
      </StyledScrollableDiv>
    </StyledPageDiv>
  );
};
